import React from 'react';
import { Box, Typography, Button, IconButton, Tooltip, Paper, alpha } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  OpenInNewRounded as OpenInNewIcon,
  ReceiptLongRounded as ReceiptIcon,
  AccountBalanceWalletRounded as WalletIcon,
  LocalShippingRounded as ShipmentIcon,
  LaunchRounded as LaunchIcon,
} from '@mui/icons-material';
import { RedirectDetails } from '../types/redirect.types';
import { buildRedirectUrl, getRedirectTitleAndLabel } from '../utils/redirectHelper';

interface RedirectCardProps {
  redirect: RedirectDetails;
  disabled?: boolean;
}

export const RedirectCard: React.FC<RedirectCardProps> = ({ redirect, disabled = false }) => {
  const navigate = useNavigate();

  if (!redirect?.url) return null;

  const { title, subtitle, iconType } = getRedirectTitleAndLabel(redirect.url);
  const fullUrl = buildRedirectUrl(redirect.url, redirect.filters, redirect.api_url, redirect.method);

  const handleRedirect = (mode: 'current' | 'new') => {
    if (disabled) return;
    if (mode === 'new') {
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    } else {
      try {
        navigate(fullUrl);
      } catch {
        window.location.href = fullUrl;
      }
    }
  };

  const renderIcon = () => {
    switch (iconType) {
      case 'invoice':
        return <ReceiptIcon sx={{ fontSize: 20, color: 'primary.main' }} />;
      case 'transaction':
        return <WalletIcon sx={{ fontSize: 20, color: 'primary.main' }} />;
      case 'shipment':
        return <ShipmentIcon sx={{ fontSize: 20, color: 'primary.main' }} />;
      default:
        return <LaunchIcon sx={{ fontSize: 20, color: 'primary.main' }} />;
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: '14px 16px',
        mt: 1.5,
        mb: 0.5,
        borderRadius: '12px',
        backgroundColor: '#FFFFFF',
        borderColor: 'rgba(0, 0, 0, 0.08)',
        borderLeft: theme => `4px solid ${theme.palette.primary.main}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            backgroundColor: theme => alpha(theme.palette.primary.main, 0.08),
            border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {renderIcon()}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: '13.5px',
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.3,
              letterSpacing: '-0.1px',
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: '11.5px',
              color: 'text.secondary',
              lineHeight: 1.3,
              display: 'block',
              mt: 0.25,
            }}
          >
            {subtitle || 'Click to view results'}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          pt: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'stretch',
            borderRadius: '8px',
            backgroundColor: disabled ? 'action.disabledBackground' : 'primary.main',
            color: disabled ? 'action.disabled' : 'primary.contrastText',
            boxShadow: disabled ? 'none' : theme => `0 2px 6px ${alpha(theme.palette.primary.main, 0.25)}`,
            overflow: 'hidden',
            transition: 'all 0.2s ease',
            ...(!disabled && {
              '&:hover': {
                backgroundColor: 'primary.dark',
                boxShadow: theme => `0 3px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
              },
            }),
          }}
        >
          <Button
            variant="text"
            size="small"
            onClick={() => handleRedirect('current')}
            disabled={disabled}
            sx={{
              textTransform: 'none',
              color: disabled ? 'inherit' : 'primary.contrastText',
              fontSize: '12px',
              fontWeight: 500,
              px: 1.75,
              py: 0.6,
              borderRadius: '8px 0 0 8px',
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: disabled ? 'transparent' : 'rgba(255, 255, 255, 0.12)',
              },
            }}
          >
            Open
          </Button>
          <Box
            sx={{
              width: '1px',
              backgroundColor: disabled ? 'action.disabled' : 'rgba(255, 255, 255, 0.25)',
              my: 0.75,
            }}
          />
          <Tooltip title="Open in new tab" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => handleRedirect('new')}
                disabled={disabled}
                aria-label="Open in new tab"
                sx={{
                  color: disabled ? 'inherit' : 'primary.contrastText',
                  borderRadius: '0 8px 8px 0',
                  px: 1,
                  py: 0.6,
                  height: '100%',
                  '&:hover': {
                    backgroundColor: disabled ? 'transparent' : 'rgba(255, 255, 255, 0.12)',
                  },
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};
